// src/components/RadarChart.js
import React, { useEffect, useState } from 'react';
import {
  RadarChart as RechartsRadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import dayjs from 'dayjs';
import api from '../api';
import { useAuth } from '../auth/AuthContext';

/* =========================
   ユーティリティ
   ========================= */
function getLatestAndAverage(arr) {
  if (!arr || arr.length === 0) return { latest: 0, average: 0 };
  const latest = Number(arr[0]);
  const past = arr.slice(1);
  const avg = past.length > 0 ? past.reduce((a, b) => a + Number(b), 0) / past.length : latest;
  return { latest, average: avg };
}

function splitLabelTwoLines(value) {
  if (value.includes('：')) {
    const [before, after] = value.split('：');
    return [before + '：', after];
  }
  const p1 = value.indexOf('（');
  if (p1 > -1) {
    return [value.slice(0, p1), value.slice(p1)]; // ← 括弧の前で改行
  }
  return [value, ''];
}

function OutsideLegend({ latestDateLabel }) {
  return (
    <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: -4 }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 12, height: 12, background: '#0072C3', opacity: 0.6, display: 'inline-block' }} />
        <span style={{ fontSize: 12 }}>
          {`今回\n（${latestDateLabel}）`}
        </span>
      </div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 12, height: 12, background: '#FF832B', opacity: 0.3, display: 'inline-block' }} />
        <span style={{ fontSize: 12 }}>過去平均</span>
      </div>
    </div>
  );
}

function useMediaQuery(query) {
  const [m, setM] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setM(e.matches);
    setM(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, [query]);
  return m;
}

/* =========================
   本体
   ========================= */
export default function RadarChart({ data }) {
  const [recentData, setRecentData] = useState(null);
  const [latestDateLabel, setLatestDateLabel] = useState(null);
  const [rawData, setRawData] = useState(null);

  const isSEish = useMediaQuery('(max-width: 380px)');
  const isMobile = useMediaQuery('(max-width: 480px)');

  useEffect(() => {
    if (!data) return; // ← 外からまだ来てないときはスキップ

    const d = data;
    setRawData(d);
    setLatestDateLabel(dayjs(d.ai_answer_date[0]).format('YYYY-MM-DD'));

    // === 各項目の平均計算 ===
    const msws1 = getLatestAndAverage(d.ai_interview_msws1_score);
    const msws2 = getLatestAndAverage(d.ai_interview_msws2_score);
    const msws3 = getLatestAndAverage(d.ai_interview_msws3_score);
    const mswsLatest = (msws1.latest + msws2.latest + msws3.latest) / 3;
    const mswsAvg = (msws1.average + msws2.average + msws3.average) / 3;

    const mapping = [
      {
        name: '歩行機能',
        latest: mswsLatest,
        average: mswsAvg,
        reference: `MSWS-12: ${d.msws_12} / T25FW: ${d.t25fw}`,
      },
      {
        name: '上肢機能',
        ...getLatestAndAverage(d.ai_interview_amsq_score),
        reference: `9HPT 利: ${d.hpt9} / 非利: ${d.hpt9_vs}`,
      },
      {
        name: '疲労（認知）',
        ...getLatestAndAverage(d.ai_interview_mfis_cog_score),
        reference: `MFIS: ${d.mfis}`,
      },
      {
        name: '疲労（身体動作）',
        ...getLatestAndAverage(d.ai_interview_mfis_phy_score),
        reference: `MFIS: ${d.mfis}`,
      },
      {
        name: '疲労（社会性）',
        ...getLatestAndAverage(d.ai_interview_mfis_soc_score),
        reference: `MFIS: ${d.mfis}`,
      },
      {
        name: '高次脳機能（情報処理速度）',
        ...getLatestAndAverage(d.ai_interview_msnq_att_score),
        reference: `SDMT: ${d.sdmt} / CogEval: ${d.cogeval} / MSNQ: ${d.msnq}`,
      },
      {
        name: '高次脳機能（記憶）',
        ...getLatestAndAverage(d.ai_interview_msnq_mem_score),
        reference: `MSNQ: ${d.msnq}`,
      },
      {
        name: '高次脳機能（その他活動）',
        ...getLatestAndAverage(d.ai_interview_msnq_other_score),
        reference: `MSNQ: ${d.msnq}`,
      },
      {
        name: '高次脳機能（人格・行動）',
        ...getLatestAndAverage(d.ai_interview_msnq_pb_score),
        reference: `MSNQ: ${d.msnq}`,
      },
    ];

    setRecentData(mapping);
  }, [data]);

  if (!recentData || !latestDateLabel) return <div>読み込み中...</div>;

  const chartHeight = isSEish ? 320 : isMobile ? 340 : 360;
  const outerRadius = isSEish ? '92%' : isMobile ? '88%' : '80%';
  const marginTop = isSEish ? -14 : isMobile ? -10 : 0;
  const marginBottom = isSEish ? -12 : isMobile ? -8 : 0;
  const cy = isSEish ? '48%' : isMobile ? '49%' : '50%';
  const tickFs = isSEish ? 10 : isMobile ? 11 : 12;
  const tickDy2 = isSEish ? '1.05em' : isMobile ? '1.1em' : '1.2em';
  const angleTickMargin = isSEish ? 4 : isMobile ? 5 : 6;

  return (
    <div style={{ border: '1px solid #ccc', borderRadius: 4, padding: '1rem' }}>
      <h1 style={{ marginTop: '0.5rem' }}>過去2週間の状態</h1>
      <div className="flex-container">
        <div className="chartContainer chartContainer--left">
          <div style={{ height: chartHeight, width: '100%', minWidth: 300, marginBottom: '0.5rem' }}>
            <ResponsiveContainer width="100%" height="100%">
              <RechartsRadarChart
                data={recentData}
                startAngle={90}
                endAngle={-270}
                margin={{ top: marginTop, right: 56, bottom: marginBottom, left: 56 }}
                outerRadius={outerRadius}
                cy={cy}
              >
                <PolarGrid />
                <PolarAngleAxis
                  dataKey="name"
                  tickLine={false}
                  tickMargin={angleTickMargin}
                  tick={({ x, y, payload }) => {
                    const rad = (payload.coordinate * Math.PI) / 180;
                    const ux = Math.cos(rad);
                    const uy = Math.sin(rad);
                    let extra = 2;
                    if (Math.abs(uy) > 0.8) extra = -2;
                    const lx = x + ux * extra;
                    const ly = y + uy * extra;
                    let anchor = 'middle';
                    if (ux > 0.3) anchor = 'start';
                    else if (ux < -0.3) anchor = 'end';
                    const [line1, line2] = splitLabelTwoLines(String(payload.value));
                    return (
                      <text x={lx} y={ly} textAnchor={anchor} fontSize={tickFs}>
                        <tspan x={lx} dy="0">{line1}</tspan>
                        {line2 ? <tspan x={lx} dy={tickDy2}>{line2}</tspan> : null}
                      </text>
                    );
                  }}
                />
                <PolarRadiusAxis angle={30} domain={[0, 10]} tickCount={6} tickFormatter={(v) => Math.round(v)} />
                <Radar name={`今回（${latestDateLabel}）`} dataKey="latest" stroke="#0072C3" fill="#0072C3" fillOpacity={0.1} />
                <Radar name="過去平均" dataKey="average" stroke="#FF832B" fill="#FF832B" fillOpacity={0.1} />
                <Tooltip formatter={(val) => Number(val).toFixed(2)} />
              </RechartsRadarChart>
            </ResponsiveContainer>
          </div>
          <OutsideLegend latestDateLabel={latestDateLabel} />
        </div>
        <div className="chartContainer chartContainer--right">
          <TableContainer>
            <Table size="sm">
              <TableHead>
                <TableRow>
                  <TableHeader>項目</TableHeader>
                  <TableHeader>{`今回（${latestDateLabel}）`}</TableHeader>
                  <TableHeader>過去平均</TableHeader>
                  <TableHeader>対応する既存尺度</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentData.map((row, idx) => {
                  // MFISブロック（3行分: 認知, 身体動作, 社会性）
                  if (row.name === '疲労（認知）') {
                    return (
                      <React.Fragment key="mfis-block">
                        <TableRow>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.latest.toFixed(2)}</TableCell>
                          <TableCell>{row.average.toFixed(2)}</TableCell>
                          <TableCell rowSpan={3}>
                            {`MFIS: ${rawData.mfis}`}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>疲労（身体動作）</TableCell>
                          <TableCell>{recentData[idx + 1].latest.toFixed(2)}</TableCell>
                          <TableCell>{recentData[idx + 1].average.toFixed(2)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>疲労（社会性）</TableCell>
                          <TableCell>{recentData[idx + 2].latest.toFixed(2)}</TableCell>
                          <TableCell>{recentData[idx + 2].average.toFixed(2)}</TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  }

                  // MSNQブロック（4行分: 情報処理速度, 記憶, その他活動, 人格・行動）
                  if (row.name === '高次脳機能（情報処理速度）') {
                    return (
                      <React.Fragment key="msnq-block">
                        <TableRow>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.latest.toFixed(2)}</TableCell>
                          <TableCell>{row.average.toFixed(2)}</TableCell>
                          <TableCell rowSpan={4}>
                            SDMT: {rawData.sdmt} /<br/>
                            CogEval: {rawData.cogeval} /<br/>
                            MSNQ: {rawData.msnq}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>高次脳機能（記憶）</TableCell>
                          <TableCell>{recentData[idx + 1].latest.toFixed(2)}</TableCell>
                          <TableCell>{recentData[idx + 1].average.toFixed(2)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>高次脳機能（その他活動）</TableCell>
                          <TableCell>{recentData[idx + 2].latest.toFixed(2)}</TableCell>
                          <TableCell>{recentData[idx + 2].average.toFixed(2)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>高次脳機能（人格・行動）</TableCell>
                          <TableCell>{recentData[idx + 3].latest.toFixed(2)}</TableCell>
                          <TableCell>{recentData[idx + 3].average.toFixed(2)}</TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  }

                  // 通常行（歩行・上肢など）
                  if (
                    !['疲労（身体動作）','疲労（社会性）',
                      '高次脳機能（記憶）','高次脳機能（その他活動）','高次脳機能（人格・行動）'
                    ].includes(row.name)
                  ) {
                    return (
                      <TableRow key={row.name}>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.latest.toFixed(2)}</TableCell>
                        <TableCell>{row.average.toFixed(2)}</TableCell>
                        <TableCell>{row.reference ?? '—'}</TableCell>
                      </TableRow>
                    );
                  }

                  return null; // まとめ済み行はスキップ
                })}
              </TableBody>

            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
}
